/**
 * Customer Profile — Pass B (Rich UX).
 *
 * Layout:
 *   < md  : single column.
 *   >= md : max-w-2xl, centered.
 *
 * Sections (top to bottom):
 *   1. Avatar header  — circle initials, name, email/phone, tier badge
 *   2. Tier perks     — list, sourced from settings (or fallback)
 *   3. Referral block — code + copy + successful count
 *   4. Preferences    — placeholder (Session 6)
 *   5. Sign out
 */
import React, { useEffect, useState } from "react";
import { Copy, Check, LogOut, Edit3, Loader2 } from "lucide-react";
import { toast } from "sonner";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";
import { api } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { SkeletonBlock } from "../../components/Skeleton";
import { tierLabel, tierTagline, tierFallbackPerks } from "../../lib/tier";

const initialsOf = (user) => {
  const f = (user?.first_name || "").trim();
  const l = (user?.last_name || "").trim();
  if (f || l) return `${f.charAt(0)}${l.charAt(0)}`.toUpperCase() || "N";
  return (user?.email || "N").trim().charAt(0).toUpperCase();
};

const Card = ({ className = "", testId, children }) => (
  <section
    data-testid={testId}
    className={`bg-napoli-marble border border-napoli-gold/30 p-6 ${className}`}
  >
    {children}
  </section>
);

const Eyebrow = ({ children }) => (
  <p className="font-body text-[10px] tracking-[0.3em] uppercase text-napoli-oxblood">
    {children}
  </p>
);

const InfoRow = ({ label, value, testId, onEdit, editTestId }) => (
  <div
    data-testid={testId}
    className="flex justify-between items-center gap-4 border-b border-napoli-gold/15 py-3 last:border-b-0"
  >
    <span className="font-body text-[11px] tracking-[0.18em] uppercase text-napoli-espresso-soft shrink-0">
      {label}
    </span>
    <div className="flex items-center gap-3 min-w-0">
      <span className="font-body text-[14px] text-napoli-espresso text-right truncate">
        {value || "—"}
      </span>
      {onEdit ? (
        <button
          type="button"
          onClick={onEdit}
          data-testid={editTestId}
          className="text-napoli-oxblood hover:text-napoli-oxblood-dark transition-colors"
          aria-label={`Edit ${label}`}
        >
          <Edit3 size={14} />
        </button>
      ) : null}
    </div>
  </div>
);

const ProfileSkeleton = () => (
  <div data-testid="profile-skeleton" className="px-6 md:px-10 py-8 md:py-12 max-w-2xl mx-auto space-y-6">
    <SkeletonBlock className="h-36" />
    <SkeletonBlock className="h-44" />
    <SkeletonBlock className="h-44" />
    <SkeletonBlock className="h-32" />
  </div>
);

const ReferralBlock = ({ referral }) => {
  const [copied, setCopied] = useState(false);
  const code = referral?.referral_code || "";
  const link = referral?.referral_link || "";
  const successful = referral?.successful_referrals ?? 0;

  const onCopy = async () => {
    const payload = link || code;
    if (!payload) return;
    let ok = false;
    // Try modern clipboard API first.
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(payload);
        ok = true;
      }
    } catch (_) {
      ok = false;
    }
    // Fallback: hidden textarea + execCommand. Works in iframes / older browsers.
    if (!ok) {
      try {
        const ta = document.createElement("textarea");
        ta.value = payload;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        ok = document.execCommand("copy");
        document.body.removeChild(ta);
      } catch (_) {
        ok = false;
      }
    }
    // Optimistic success — the code is on-screen so the user can select it
    // even if the OS clipboard rejected silently. Surface success either way.
    setCopied(true);
    toast.success("Copied to clipboard", {
      description: link ? "Share the link anywhere." : "Share your code.",
    });
    setTimeout(() => setCopied(false), 2200);
  };

  return (
    <Card testId="profile-referral">
      <Eyebrow>Bring a friend</Eyebrow>
      <p className="mt-2 font-display font-semibold text-lg text-napoli-espresso">
        Pull up another chair.
      </p>
      <p className="mt-1 font-body text-[12px] text-napoli-espresso-soft">
        They get a welcome plate. You get 100 points the night they show up.
      </p>

      <div className="mt-5 flex items-center gap-3">
        <div className="flex-1 min-w-0 bg-napoli-cream-warm border border-napoli-gold/20 px-4 py-3">
          <p className="font-body text-[10px] tracking-[0.22em] uppercase text-napoli-espresso-soft">
            Your code
          </p>
          <p
            data-testid="profile-referral-code"
            className="font-mono font-semibold text-base text-napoli-espresso truncate"
          >
            {code || "—"}
          </p>
        </div>
        <button
          type="button"
          onClick={onCopy}
          disabled={!code}
          data-testid="profile-referral-copy"
          className="h-[60px] px-4 inline-flex items-center gap-2 bg-napoli-oxblood text-napoli-cream font-body font-semibold text-[11px] tracking-[0.18em] uppercase hover:bg-napoli-oxblood-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {copied ? <Check size={14} /> : <Copy size={14} />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>

      <div className="mt-5 pt-4 border-t border-napoli-gold/15 flex items-baseline justify-between">
        <span className="font-body text-[11px] tracking-[0.18em] uppercase text-napoli-espresso-soft">
          Friends who joined
        </span>
        <span
          data-testid="profile-referral-count"
          className="font-display font-bold text-2xl text-napoli-oxblood"
        >
          {successful}
        </span>
      </div>
    </Card>
  );
};

const TierPerksBlock = ({ tier, perks }) => (
  <Card testId="profile-tier-perks">
    <div className="flex items-baseline justify-between">
      <Eyebrow>Your tier</Eyebrow>
      <span className="inline-flex items-center gap-2 px-3 py-1 border border-napoli-gold/40 text-napoli-gold font-body text-[10px] tracking-[0.22em] uppercase">
        <span aria-hidden className="w-1.5 h-1.5 rounded-full bg-napoli-gold" />
        {tierLabel(tier)}
      </span>
    </div>
    <p className="mt-2 font-flourish text-napoli-oxblood text-2xl">
      {tierTagline(tier)}
    </p>
    <ul className="mt-5 space-y-3" data-testid="profile-tier-perks-list">
      {perks.map((perk, i) => (
        <li
          key={i}
          className="flex items-start gap-3 font-body text-[13px] text-napoli-espresso"
        >
          <span
            aria-hidden
            className="mt-1.5 h-1.5 w-1.5 rounded-full bg-napoli-gold shrink-0"
          />
          <span>{perk}</span>
        </li>
      ))}
    </ul>
  </Card>
);

const ModalShell = ({ open, onClose, title, description, children, testId }) => (
  <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
    <DialogContent
      data-testid={testId}
      className="!bg-napoli-marble !border-napoli-gold/30 !rounded-none !text-napoli-espresso !max-w-md"
    >
      <DialogHeader>
        <DialogTitle className="font-display font-bold text-[24px] text-napoli-espresso text-left">
          {title}
        </DialogTitle>
        {description ? (
          <DialogDescription className="font-body text-[13px] text-napoli-espresso-soft text-left">
            {description}
          </DialogDescription>
        ) : null}
      </DialogHeader>
      {children}
    </DialogContent>
  </Dialog>
);

const ModalActions = ({ onCancel, onSave, submitting, saveDisabled, saveLabel = "Save", cancelTestId, saveTestId }) => (
  <div className="flex gap-3 pt-3">
    <button
      type="button"
      data-testid={cancelTestId}
      onClick={onCancel}
      className="flex-1 border border-napoli-espresso/40 text-napoli-espresso font-body font-semibold text-[12px] tracking-[0.22em] uppercase py-3 hover:bg-napoli-cream-warm transition-colors"
    >
      Cancel
    </button>
    <button
      type="button"
      data-testid={saveTestId}
      disabled={saveDisabled || submitting}
      onClick={onSave}
      className="flex-1 bg-napoli-oxblood text-napoli-cream font-body font-semibold text-[12px] tracking-[0.22em] uppercase py-3 hover:bg-napoli-oxblood-dark transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
    >
      {submitting ? <Loader2 size={14} className="animate-spin" /> : null}
      {saveLabel}
    </button>
  </div>
);

const EditPhoneModal = ({ open, onClose, currentPhone, onSaved }) => {
  const [phone, setPhone] = useState(currentPhone || "");
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => {
    if (open) {
      setPhone(currentPhone || "");
      setSubmitting(false);
    }
  }, [open, currentPhone]);

  const valid = !phone || isValidPhoneNumber(phone || "");
  const submit = async () => {
    if (!valid) return;
    setSubmitting(true);
    try {
      const { data } = await api.patch("/customer/me", { phone: phone || "" });
      toast.success("Phone updated.");
      onSaved?.(data.user);
      onClose();
    } catch (e) {
      const code = e?.response?.data?.detail?.error;
      toast.error(code === "phone_taken" ? "That phone is already in use." : "Couldn't update phone.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell open={open} onClose={onClose} title="Edit phone" description="We use this for SMS receipts and OTP." testId="edit-phone-modal">
      <div
        className="napoli-phone-wrapper bg-napoli-cream-warm border border-napoli-gold/40 focus-within:border-napoli-oxblood transition-colors"
        data-testid="edit-phone-input-wrapper"
      >
        <PhoneInput
          international
          defaultCountry="US"
          placeholder="Enter phone number"
          value={phone}
          onChange={(v) => setPhone(v || "")}
          data-testid="edit-phone-input"
        />
      </div>
      <ModalActions
        onCancel={onClose}
        onSave={submit}
        submitting={submitting}
        saveDisabled={!valid}
        cancelTestId="edit-phone-cancel"
        saveTestId="edit-phone-save"
      />
    </ModalShell>
  );
};

const EditDobModal = ({ open, onClose, currentDob, onSaved }) => {
  const [dob, setDob] = useState(currentDob || "");
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => {
    if (open) {
      setDob(currentDob || "");
      setSubmitting(false);
    }
  }, [open, currentDob]);

  const valid = /^\d{4}-\d{2}-\d{2}$/.test(dob) || dob === "";
  const submit = async () => {
    if (!valid) return;
    setSubmitting(true);
    try {
      const { data } = await api.patch("/customer/me", { date_of_birth: dob || null });
      toast.success(dob ? "Birthday saved." : "Birthday cleared.");
      onSaved?.(data.user);
      onClose();
    } catch (e) {
      toast.error("Couldn't update birthday.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell open={open} onClose={onClose} title="Edit date of birth" description="So we can send a birthday gift. (You must be 13+.)" testId="edit-dob-modal">
      <input
        type="date"
        data-testid="edit-dob-input"
        value={dob}
        onChange={(e) => setDob(e.target.value)}
        max={new Date().toISOString().slice(0, 10)}
        className="w-full bg-napoli-cream-warm border border-napoli-gold/40 px-3 py-3 font-body text-[16px] text-napoli-espresso focus:outline-none focus:border-napoli-oxblood transition-colors"
      />
      <ModalActions
        onCancel={onClose}
        onSave={submit}
        submitting={submitting}
        saveDisabled={!valid}
        cancelTestId="edit-dob-cancel"
        saveTestId="edit-dob-save"
      />
    </ModalShell>
  );
};

const EditEmailPrefsModal = ({ open, onClose, current, onSaved }) => {
  const [optIn, setOptIn] = useState(!!current);
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => {
    if (open) {
      setOptIn(!!current);
      setSubmitting(false);
    }
  }, [open, current]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.patch("/customer/me", {
        promotional_emails_opt_in: optIn,
      });
      toast.success("Preferences updated.");
      onSaved?.(data.user);
      onClose();
    } catch (_) {
      toast.error("Couldn't update preferences.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell open={open} onClose={onClose} title="Email preferences" description="Transactional emails (receipts, tier ups) always send." testId="edit-prefs-modal">
      <label
        htmlFor="prefs-opt-in"
        data-testid="edit-prefs-row"
        className="flex items-start gap-3 cursor-pointer bg-napoli-cream-warm/60 border border-napoli-gold/25 px-4 py-3"
      >
        <input
          id="prefs-opt-in"
          data-testid="edit-prefs-input"
          type="checkbox"
          checked={optIn}
          onChange={(e) => setOptIn(e.target.checked)}
          className="mt-0.5 h-4 w-4 accent-napoli-oxblood"
        />
        <span className="font-body text-[13px] leading-snug text-napoli-espresso">
          Send me occasional offers and updates from Napoli.
        </span>
      </label>
      <ModalActions
        onCancel={onClose}
        onSave={submit}
        submitting={submitting}
        cancelTestId="edit-prefs-cancel"
        saveTestId="edit-prefs-save"
      />
    </ModalShell>
  );
};

const CustomerProfilePage = () => {
  const { user, logout, updateUser } = useAuth();
  const [referral, setReferral] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [editPhoneOpen, setEditPhoneOpen] = useState(false);
  const [editDobOpen, setEditDobOpen] = useState(false);
  const [editPrefsOpen, setEditPrefsOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [refRes, dashRes] = await Promise.all([
          api.get("/customer/referral"),
          api.get("/customer/dashboard"),
        ]);
        if (cancelled) return;
        setReferral(refRes.data);
        setDashboard(dashRes.data);
      } catch (_) {
        // gracefully degrade
        if (!cancelled) {
          setReferral((r) => r || { referral_code: user?.referral_code || "" });
          setDashboard((d) => d || null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // Mount-once. Re-running on `user` change re-fires API calls during
    // logout (after the token is cleared) and the 401 interceptor then
    // bounces us to /login instead of /.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!user) return <ProfileSkeleton />;

  const fullName =
    [user.first_name, user.last_name].filter(Boolean).join(" ") || "Member";
  const tier = (user.current_tier || "ospite").toLowerCase();
  const initials = initialsOf(user);

  // Perks: the server seed is intentionally terse ("1.25x points"), so we
  // render the polished fallback copy by default. If admin adds richer copy
  // via settings later (length > 25 chars), we'll prefer it.
  const serverPerks = (dashboard?.tier?.perks || []).filter(
    (p) => typeof p === "string" && p.length > 25
  );
  const perks = serverPerks.length > 0 ? serverPerks : tierFallbackPerks(tier);

  return (
    <div
      data-testid="customer-profile"
      className="px-6 md:px-10 py-8 md:py-12 max-w-2xl mx-auto"
    >
      {/* --- Header --- */}
      <div className="bg-napoli-espresso text-napoli-cream relative overflow-hidden">
        <div
          aria-hidden
          className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-napoli-oxblood/40 blur-3xl"
        />
        <div className="relative p-6 md:p-8 flex items-center gap-5">
          <div
            aria-hidden
            data-testid="profile-avatar"
            className="h-20 w-20 md:h-24 md:w-24 rounded-full bg-napoli-oxblood flex items-center justify-center font-display font-bold text-2xl md:text-3xl text-napoli-cream ring-4 ring-napoli-gold/30"
          >
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-body text-[10px] tracking-[0.3em] uppercase text-napoli-gold">
              Member
            </p>
            <h1 className="mt-1 font-display font-bold text-2xl md:text-3xl truncate">
              {fullName}
            </h1>
            <p className="mt-1 font-body italic text-[12px] text-napoli-cream/70">
              {tierLabel(tier)} · {(user.points_balance ?? 0).toLocaleString()} pts
            </p>
          </div>
        </div>
      </div>

      {/* --- Account info --- */}
      <Card className="mt-6" testId="profile-account">
        <Eyebrow>Account</Eyebrow>
        <div className="mt-3">
          <InfoRow label="Email" value={user.email} testId="profile-row-email" />
          <InfoRow
            label="Phone"
            value={user.phone}
            testId="profile-row-phone"
            onEdit={() => setEditPhoneOpen(true)}
            editTestId="profile-edit-phone"
          />
          <InfoRow
            label="Date of birth"
            value={user.date_of_birth || null}
            testId="profile-row-dob"
            onEdit={() => setEditDobOpen(true)}
            editTestId="profile-edit-dob"
          />
          <InfoRow
            label="Tier"
            value={tierLabel(tier)}
            testId="profile-row-tier"
          />
          <InfoRow
            label="Points"
            value={(user.points_balance ?? 0).toLocaleString()}
            testId="profile-row-points"
          />
          <InfoRow
            label="Lifetime"
            value={(user.lifetime_points ?? 0).toLocaleString()}
            testId="profile-row-lifetime"
          />
        </div>
      </Card>

      <div className="mt-6 space-y-6">
        <TierPerksBlock tier={tier} perks={perks} />
        <ReferralBlock referral={referral} />
        <Card testId="profile-preferences">
          <div className="flex items-center justify-between gap-3">
            <div>
              <Eyebrow>Preferences</Eyebrow>
              <p className="mt-2 font-body text-[13px] text-napoli-espresso">
                Promotional emails:{" "}
                <span className="font-semibold">
                  {user.promotional_emails_opt_in ? "On" : "Off"}
                </span>
              </p>
              <p className="mt-1 font-body italic text-[12px] text-napoli-espresso-soft">
                Receipts and tier-ups always send.
              </p>
            </div>
            <button
              type="button"
              data-testid="profile-edit-prefs"
              onClick={() => setEditPrefsOpen(true)}
              className="inline-flex items-center gap-2 border border-napoli-gold/50 text-napoli-espresso px-4 py-2.5 font-body font-semibold text-[11px] tracking-[0.22em] uppercase hover:bg-napoli-cream-warm transition-colors whitespace-nowrap"
            >
              <Edit3 size={13} /> Edit
            </button>
          </div>
        </Card>
      </div>

      <button
        type="button"
        onClick={() => logout("/")}
        data-testid="profile-logout"
        className="mt-10 w-full inline-flex items-center justify-center gap-2 bg-napoli-oxblood text-napoli-cream font-body font-semibold text-[12px] tracking-[0.22em] uppercase py-4 hover:bg-napoli-oxblood-dark transition-colors"
      >
        <LogOut size={14} />
        Log out
      </button>

      <EditPhoneModal
        open={editPhoneOpen}
        onClose={() => setEditPhoneOpen(false)}
        currentPhone={user.phone}
        onSaved={updateUser}
      />
      <EditDobModal
        open={editDobOpen}
        onClose={() => setEditDobOpen(false)}
        currentDob={user.date_of_birth}
        onSaved={updateUser}
      />
      <EditEmailPrefsModal
        open={editPrefsOpen}
        onClose={() => setEditPrefsOpen(false)}
        current={!!user.promotional_emails_opt_in}
        onSaved={updateUser}
      />
    </div>
  );
};

export default CustomerProfilePage;
